import pick from 'lodash/pick';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import {
  initiatePrivileged,
  transitionPrivileged,
  validateDiscountApi,
  triggerPostBookingWelcomeMessage,
} from '../../util/api';
import { markExpectWelcomeMessage } from '../../util/expectWelcomeMessage';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { setDiscountCodeSearchParam } from '../../util/discountCode';
import { sendTrackingEvent, TRACKING_EVENTS } from '../../analytics/trackingEvent';
import {
  isDigitalProductsProcessName,
  resolveLatestProcessName,
} from '../../transactions/transaction';

const debugLog = (location, message, data, hypothesisId) => {
  // #region agent log
  fetch('http://127.0.0.1:7394/ingest/3c6032a8-77b8-448f-ad04-925b8d9d06b6', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '70240a' },
    body: JSON.stringify({
      sessionId: '70240a',
      runId: 'checkout-payment-flow',
      hypothesisId,
      location,
      message,
      data,
      timestamp: Date.now(),
    }),
  }).catch(() => {});
  // #endregion
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const VALIDATE_DISCOUNT_REQUEST = 'app/CheckoutPage/VALIDATE_DISCOUNT_REQUEST';
export const VALIDATE_DISCOUNT_SUCCESS = 'app/CheckoutPage/VALIDATE_DISCOUNT_SUCCESS';
export const VALIDATE_DISCOUNT_ERROR = 'app/CheckoutPage/VALIDATE_DISCOUNT_ERROR';
export const CLEAR_DISCOUNT = 'app/CheckoutPage/CLEAR_DISCOUNT';
export const CLEAR_CHECKOUT_ORDER_ERRORS = 'app/CheckoutPage/CLEAR_CHECKOUT_ORDER_ERRORS';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  validateDiscountInProgress: false,
  validateDiscountError: null,
  appliedDiscount: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      // Check that the local devices clock is within a minute from the server
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
      };
    }
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      // Keep stripeCustomerFetched true if already loaded so a redundant refetch
      // (e.g. from a remount) does not unmount Stripe Elements mid-checkout.
      return state.stripeCustomerFetched ? state : { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case VALIDATE_DISCOUNT_REQUEST:
      return {
        ...state,
        validateDiscountInProgress: true,
        validateDiscountError: null,
      };
    case VALIDATE_DISCOUNT_SUCCESS:
      return {
        ...state,
        validateDiscountInProgress: false,
        appliedDiscount: payload.discount,
        validateDiscountError: null,
      };
    case VALIDATE_DISCOUNT_ERROR:
      return {
        ...state,
        validateDiscountInProgress: false,
        validateDiscountError: payload,
        appliedDiscount: null,
      };
    case CLEAR_DISCOUNT:
      return {
        ...state,
        appliedDiscount: null,
        validateDiscountError: null,
      };
    case CLEAR_CHECKOUT_ORDER_ERRORS:
      return {
        ...state,
        initiateOrderError: null,
        speculateTransactionError: null,
        confirmPaymentError: null,
        speculatedTransaction: null,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

const validateDiscountRequest = () => ({ type: VALIDATE_DISCOUNT_REQUEST });
const validateDiscountSuccess = discount => ({
  type: VALIDATE_DISCOUNT_SUCCESS,
  payload: { discount },
});
const validateDiscountError = error => ({
  type: VALIDATE_DISCOUNT_ERROR,
  payload: error,
});
export const clearDiscount = () => ({ type: CLEAR_DISCOUNT });
export const clearCheckoutOrderErrors = () => ({ type: CLEAR_CHECKOUT_ORDER_ERRORS });

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    bookingDates,
    userTryOnDiscountCode,
    tierId,
    ...otherOrderParams
  } = orderParams;
  const processName = processAlias ? resolveLatestProcessName(processAlias.split('/')[0]) : null;
  const quantityMaybe =
    quantity && processName && isDigitalProductsProcessName(processName)
      ? { quantity }
      : quantity
      ? { stockReservationQuantity: quantity }
      : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server (merged with transition params in initiate-privileged)
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(userTryOnDiscountCode ? { userTryOnDiscountCode } : {}),
    ...(tierId ? { tierId } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    debugLog(
      'CheckoutPage.duck.js:initiateOrder:success',
      'initiate order success',
      {
        txId: order?.id?.uuid,
        lastTransition: order?.attributes?.lastTransition,
        txState: order?.attributes?.state,
      },
      'H3'
    );
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    // Free/instant privileged paths may send welcome server-side; Order Details will poll.
    markExpectWelcomeMessage(order.id);
    return order;
  };

  const handleError = e => {
    debugLog(
      'CheckoutPage.duck.js:initiateOrder:error',
      'initiate order failed',
      {
        status: e?.status,
        statusText: e?.statusText,
        apiCodes: e?.data?.errors?.map(err => err.code) || [],
        apiStatuses: e?.data?.errors?.map(err => err.status) || [],
        stripeMessages: e?.data?.errors?.map(err => err?.meta?.stripeMessage).filter(Boolean) || [],
      },
      'H4'
    );
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      debugLog(
        'CheckoutPage.duck.js:confirmPayment:success',
        'confirm payment success',
        {
          txId: order?.id?.uuid,
          lastTransition: order?.attributes?.lastTransition,
          txState: order?.attributes?.state,
        },
        'H5'
      );
      dispatch(confirmPaymentSuccess(order.id));
      // Fire-and-forget so Order Details can show typing + poll immediately.
      // retryOnSkip: worker may have claimed first; client retries after settle.
      markExpectWelcomeMessage(order.id);
      triggerPostBookingWelcomeMessage(order.id, {
        delayMs: 800,
        retryOnSkip: true,
        retryDelayMs: 5000,
      }).catch(() => {
        // Purchase must not fail if messaging trigger fails.
      });
      return order;
    })
    .catch(e => {
      debugLog(
        'CheckoutPage.duck.js:confirmPayment:error',
        'confirm payment failed',
        {
          status: e?.status,
          statusText: e?.statusText,
          apiCodes: e?.data?.errors?.map(err => err.code) || [],
          apiStatuses: e?.data?.errors?.map(err => err.status) || [],
          stripeMessages:
            e?.data?.errors?.map(err => err?.meta?.stripeMessage).filter(Boolean) || [],
        },
        'H5'
      );
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    bookingDates,
    userTryOnDiscountCode,
    tierId,
    ...otherOrderParams
  } = orderParams;
  const processName = processAlias ? resolveLatestProcessName(processAlias.split('/')[0]) : null;
  const quantityMaybe =
    quantity && processName && isDigitalProductsProcessName(processName)
      ? { quantity }
      : quantity
      ? { stockReservationQuantity: quantity }
      : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(userTryOnDiscountCode ? { userTryOnDiscountCode } : {}),
    ...(tierId ? { tierId } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// Add thunk for validating discount
export const validateDiscount = (listingId, userTryOnDiscountCode, options = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(validateDiscountRequest());

  const bodyParams = { listingId, userTryOnDiscountCode };

  return validateDiscountApi(bodyParams)
    .then(response => {
      const { isValid, discount, error } = response.data;

      if (isValid) {
        sendTrackingEvent({
          name: TRACKING_EVENTS.USER_TRY_ON_DISCOUNT_CODE_VALIDATION_SUCCEEDED,
          parameters: {
            listingId: listingId?.uuid,
            userTryOnDiscountCode: userTryOnDiscountCode,
          },
        });

        dispatch(validateDiscountSuccess(discount));

        if (options.skipReload) {
          return discount;
        }

        // This is a temp solution
        // Add "userTryOnDiscountCode" to query params and reload the page
        // In other pages the param will be picked and applied to calls to API
        const url = setDiscountCodeSearchParam(window.location, userTryOnDiscountCode);
        const router = getState().router;
        if (router && router.history) {
          router.history.push(url);
        } else {
          window.location.href = url;
        }
      } else {
        dispatch(validateDiscountError(error || 'Invalid discount code'));

        sendTrackingEvent({
          name: TRACKING_EVENTS.USER_TRY_ON_DISCOUNT_CODE_VALIDATION_FAILED,
          parameters: {
            listingId: listingId?.uuid,
            userTryOnDiscountCode: userTryOnDiscountCode,
          },
        });
      }
    })
    .catch(e => {
      const errorMessage = e.message || 'Failed to validate discount code';
      dispatch(validateDiscountError(errorMessage));
      throw e;
    });
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const fetchCheckoutListing = listingId => (dispatch, getState, sdk) => {
  const id = listingId?.uuid || listingId;
  if (!id) {
    return Promise.resolve(null);
  }
  return sdk.listings
    .show({
      id,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
    })
    .then(response => {
      const listing = denormalisedResponseEntities(response)[0];
      return listing || null;
    })
    .catch(e => {
      log.error(e, 'checkout-page-refresh-listing-failed', { listingId: id });
      return null;
    });
};

export const loadData = params => (dispatch, getState, sdk) => {
  const listingId = params?.id;
  if (!listingId) {
    return Promise.resolve(null);
  }
  const existing = getState()?.CheckoutPage?.listing;
  const existingOrderData = getState()?.CheckoutPage?.orderData;
  if (existing?.id?.uuid === listingId && existingOrderData) {
    return Promise.resolve(null);
  }
  return sdk.listings
    .show({
      id: listingId,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
    })
    .then(response => {
      const listing = denormalisedResponseEntities(response)[0];
      if (!listing) {
        return response;
      }
      dispatch(setInitialValues({ listing, orderData: { quantity: 1 } }));
      return response;
    })
    .catch(e => {
      log.error(e, 'checkout-page-show-listing-failed', { listingId });
      return null;
    });
};
